/**
 * Public accommodation section — reusable across Place, Area, and Landing pages.
 * Follows hideIfEmpty principle: renders nothing when no accommodations exist.
 * Uses the existing AccommodationCard pattern from ProducerAccommodations.
 */
import { Link } from "react-router-dom";
import { Bed, Users, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useCoverImageUrl } from "@/hooks/useMediaAssets";
import type { PublicAccommodationItem } from "@/hooks/usePublicAccommodations";

function formatPriceLabel(text: string | null | undefined): string | null {
  if (!text) return null;
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (/kr|per|natt/i.test(trimmed)) return trimmed;
  if (/^\d+$/.test(trimmed)) return `Från ${trimmed} kr per natt`;
  return trimmed;
}

function PublicAccommodationCard({
  acc,
  context,
}: {
  acc: PublicAccommodationItem;
  context?: string | null;
}) {
  const coverUrl = useCoverImageUrl("accommodation", acc.id, 640);
  const priceLabel = formatPriceLabel(acc.price_per_night_text);

  return (
    <Link to={`/accommodation/${acc.id}`} className="block group">
      <Card className="h-full transition-shadow group-hover:shadow-md overflow-hidden rounded-xl">
        {coverUrl ? (
          <div className="aspect-[16/10] overflow-hidden">
            <img
              src={coverUrl}
              alt={acc.title}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
              loading="lazy"
            />
          </div>
        ) : (
          <div className="aspect-[16/10] bg-muted flex items-center justify-center">
            <Bed className="h-8 w-8 text-muted-foreground/40" />
          </div>
        )}

        <CardContent className="pt-4 pb-4 space-y-2">
          <h3 className="font-medium text-foreground group-hover:text-primary transition-colors line-clamp-1">
            {acc.title}
          </h3>

          {context && (
            <p className="text-xs text-muted-foreground">{context}</p>
          )}

          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            {acc.beds != null && acc.beds > 0 && (
              <span className="flex items-center gap-1">
                <Bed className="h-3 w-3" /> {acc.beds} bäddar
              </span>
            )}
            {acc.guest_capacity != null && acc.guest_capacity > 0 && (
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" /> Max {acc.guest_capacity}
              </span>
            )}
          </div>

          {acc.description_short && (
            <p className="text-sm text-muted-foreground line-clamp-2">
              {acc.description_short}
            </p>
          )}

          {priceLabel ? (
            <p className="text-sm font-medium text-foreground">{priceLabel}</p>
          ) : (
            <p className="text-xs text-muted-foreground italic">
              Pris visas vid förfrågan
            </p>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

interface AccommodationSectionProps {
  accommodations: PublicAccommodationItem[];
  title?: string;
  /** Optional context string resolver per accommodation (e.g. producer name) */
  getContext?: (acc: PublicAccommodationItem) => string | null;
  className?: string;
}

/**
 * Renders a calm, editorial accommodation section.
 * Returns null if no accommodations.
 */
export function AccommodationSection({
  accommodations,
  title = "Bo här",
  getContext,
  className = "",
}: AccommodationSectionProps) {
  if (!accommodations || accommodations.length === 0) return null;

  return (
    <section className={className}>
      {title && (
        <h2 className="font-display text-lg font-medium text-foreground mb-4">
          {title}
        </h2>
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {accommodations.map((acc) => (
          <PublicAccommodationCard
            key={acc.id}
            acc={acc}
            context={getContext?.(acc)}
          />
        ))}
      </div>
    </section>
  );
}
